import { apiFetch, isApiMode } from './http';
import { REAUTHENTICATION_REQUIRED } from './reauthenticationSignal';

// ─────────────────────────────────────────────────────────────────────
// THE ORGANIZATION'S OWN «TILGANGER» PAGE — the client (slice 3, 2026-10-18).
//
// `/organization/access` is tenant-scoped: the organization is the one the
// portal is acting in (the tenant header `apiFetch` sets), never a parameter.
// Everything here is a VIEW of what the server decided. The page mirrors the
// server's ceiling so it does not offer a control the API will refuse — but
// the refusal is the API's, always. The rules it enforces, each a code here:
//
//   1A  only somebody already linked to this organization can be given a
//       role (`not_in_organization`) — no invitations from this page;
//   2B  nobody hands out, or takes away, a right they do not hold themselves
//       (`beyond_own_authority`, with the missing capabilities named);
//   3A  the last person who may hand out roles cannot be removed
//       (`last_access_manager`).
// ─────────────────────────────────────────────────────────────────────

/**
 * The capability every verb on `/organization/access` asks for.
 *
 * The server's route exports the same literal
 * (`apps/api/src/routes/organizationAccess.ts`), and
 * `organization-access-gate.test.tsx` reads that file to keep the two
 * spellings identical. The browser uses it for ONE thing: deciding whether to
 * DRAW the «Tilganger» entry in the organization's settings. It decides
 * nothing — the server refuses the route to everybody else regardless.
 */
export const ORGANIZATION_ACCESS_MANAGE = 'organization.access.manage';

export interface AccessRoleRef {
  id: string;
  key: string;
  /** The role's name as the organization sees it in `/sa` — Norwegian, from the row. */
  name: string;
}

export interface AccessPerson {
  userId: string;
  displayName: string;
  /** The account's status: `active`, or disabled. A disabled account keeps its rows. */
  status: string;
  /** On the organization's player roster, as an account (the 1A link). */
  onRoster: boolean;
  roles: AccessRoleRef[];
}

export interface AccessRole extends AccessRoleRef {
  description: string | null;
  appliesTo: string | null;
  isSystem: boolean;
  /**
   * The capabilities this role carries that the CALLER does not hold (rule
   * 2B). Empty means the caller may grant and revoke it. Otherwise the page
   * shows the ceiling — and why — rather than letting the person discover it
   * on use.
   */
  beyondYou: string[];
  /** This organization's OWN role (2026-10-31), which the page's editor may change. */
  own?: boolean;
  /** An own role's capabilities; never sent for a Starpoint role. */
  capabilities?: string[];
}

export interface OrganizationAccessList {
  organization: { id: string; kind: string };
  /** Role holders and roster members with an account, folded by user, in the server's order. */
  people: AccessPerson[];
  /** The roles this organization may hand out — its kind, its availability, never PLATFORM or LINK. */
  roles: AccessRole[];
  /**
   * What an own role may carry when THIS caller writes it: the organization's
   * ceiling, never a reserved name, nothing beyond the caller (2B). A view of
   * the server's rule — a write re-makes it and the page shows its refusal.
   * Absent on a server older than the editor: then nothing is offered.
   */
  ownRoles?: { offerable: string[] };
}

export type AccessListResult =
  /** The API is not wired up in this build (VITE_API_URL unset). */
  | { state: 'no-api' }
  | { state: 'ok'; list: OrganizationAccessList }
  /** No session, or one without `organization.access.manage` in this organization. */
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

/** Who holds what here, and what may be handed out. Never throws; the page renders the outcome. */
export async function fetchOrganizationAccess(): Promise<AccessListResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch('/organization/access');
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    return { state: 'ok', list: (await res.json()) as OrganizationAccessList };
  } catch {
    return { state: 'error', status: null };
  }
}

export type AccessWriteResult =
  /**
   * `changed` is false when the server had nothing to do — the role was
   * already held, or already absent. The end state is the one asked for
   * either way; the page says which sentence is true.
   */
  | { state: 'ok'; changed: boolean }
  | { state: 'forbidden' }
  /**
   * A machine code from the API — never a sentence; the page translates it.
   * `missing` is filled for `beyond_own_authority`: the capabilities the
   * caller lacks, so the refusal can say what rather than only that.
   */
  | {
      state: 'refused';
      reason: string;
      missing: string[];
      /** `role_in_use`: how many hold the role the page tried to delete. */
      holders?: number;
    }
  | { state: 'error'; status: number | null };

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** The code rule 2B answers with. A 403 carrying it is a refusal to translate, not a missing session. */
const BEYOND_OWN_AUTHORITY = 'beyond_own_authority';

/** The strings in a list field of a refusal body, or none. */
const stringsIn = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : [];

/** A non-2xx answer from a write on this page, as the page reads it. */
function refusalOf(
  res: Response,
  body: Record<string, unknown> | null,
  /** Read the rights a 400/409 names — the role editor's writes; grant and revoke never did. */
  namesRights = false,
): Exclude<AccessWriteResult, { state: 'ok' }> {
  const reason = typeof body?.error === 'string' ? body.error : null;
  // 2B IS A 403 WITH A NAME. The guard's 403 means «not an access manager
  // here»; this one means «an access manager, reaching past their own
  // rights». Told apart by the code, because the page says different things.
  if (res.status === 403 && reason === BEYOND_OWN_AUTHORITY) {
    return { state: 'refused', reason, missing: stringsIn(body?.missing) };
  }
  // «SIGN IN AGAIN» IS NOT «NOT YOURS» (#976 decision 2): granting and
  // revoking want a sign-in from the last fifteen minutes. The transport has
  // already raised the step-up prompt; the page says why its change did not
  // happen instead of telling an access manager they are not one.
  if (res.status === 403 && reason === REAUTHENTICATION_REQUIRED) {
    return { state: 'refused', reason, missing: [] };
  }
  if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
  if (res.status === 400 || res.status === 404 || res.status === 409) {
    // WHICH rights, when the refusal names them — the ceiling's
    // `missing`, the reserved `capability`, the last holder's `capabilities`
    // — so the page can say what, not only that.
    const named = namesRights
      ? [
          ...stringsIn(body?.missing),
          ...stringsIn(body?.capabilities),
          ...(typeof body?.capability === 'string' ? [body.capability] : []),
        ]
      : [];
    return {
      state: 'refused',
      reason: reason ?? 'refused',
      missing: named,
      ...(typeof body?.holders === 'number' ? { holders: body.holders } : {}),
    };
  }
  return { state: 'error', status: res.status };
}

async function write(
  path: string,
  init: RequestInit,
  changedField: 'granted' | 'removed',
): Promise<AccessWriteResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, init);
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok) return { state: 'ok', changed: body?.[changedField] === true };
    return refusalOf(res, body);
  } catch {
    return { state: 'error', status: null };
  }
}

/** Give somebody already linked to this organization a role it may hand out. */
export function grantOrganizationAccess(
  userId: string,
  roleId: string,
): Promise<AccessWriteResult> {
  return write(
    '/organization/access/members',
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ userId, roleId }) },
    'granted',
  );
}

/** Take a role away. The server refuses the last access manager (3A). */
export function revokeOrganizationAccess(
  userId: string,
  roleId: string,
): Promise<AccessWriteResult> {
  return write(
    `/organization/access/members/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleId)}`,
    { method: 'DELETE' },
    'removed',
  );
}

// ── THE ORGANIZATION'S OWN ROLES (the editor, 2026-11-01) ──────────────
//
// `/organization/access/roles` under the same capability, and the same rule
// as everything above: the page offers what `ownRoles.offerable` says a write
// would accept, and the server's answer — a code — is what it shows when the
// two disagree. `changed` is always true on success: these writes name what
// they set, and the page re-reads the list either way.

async function roleWrite(path: string, init: RequestInit): Promise<AccessWriteResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, init);
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok) return { state: 'ok', changed: true };
    return refusalOf(res, body, true);
  } catch {
    return { state: 'error', status: null };
  }
}

const rolePath = (roleId: string) => `/organization/access/roles/${encodeURIComponent(roleId)}`;

/** Define a role of the organization's own. */
export function createOwnRole(input: {
  name: string;
  description: string | null;
  capabilities: string[];
}): Promise<AccessWriteResult> {
  return roleWrite('/organization/access/roles', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

/** Rename or re-describe an own role. Display only — its rights are unchanged. */
export function renameOwnRole(
  roleId: string,
  patch: { name?: string; description?: string | null },
): Promise<AccessWriteResult> {
  return roleWrite(rolePath(roleId), {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  });
}

/** Replace an own role's capabilities with exactly this set. */
export function setOwnRoleCapabilities(
  roleId: string,
  capabilities: string[],
): Promise<AccessWriteResult> {
  return roleWrite(`${rolePath(roleId)}/capabilities`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ capabilities }),
  });
}

/** Delete an own role. Refused (`role_in_use`) while anybody holds it. */
export function deleteOwnRole(roleId: string): Promise<AccessWriteResult> {
  return roleWrite(rolePath(roleId), { method: 'DELETE' });
}
